import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";

const features = [
  {
    title: "Centralized Issue Dashboard",
    description: "Get a bird's-eye view of all reported issues in one clean, sortable table. Filter by category, status, or location to prioritize your work.",
    icon: "https://storage.googleapis.com/fenado-ai-farm-public/generated/982c1818-8950-4b1a-9c83-a637905cd83f.png"
  },
  {
    title: "Rich Issue Details",
    description: "Instantly access submitted photos, exact geo-coordinates, and user comments for any report. Understand the problem completely without leaving your desk.",
    icon: "https://storage.googleapis.com/fenado-ai-farm-public/generated/5afcbbbc-c189-4f81-8347-93e4d76cc07c.png"
  },
  {
    title: "Seamless Communication",
    description: "Keep citizens and internal teams in the loop. Send updates directly via Email, SMS, or WhatsApp to single or multiple recipients with just a few clicks.",
    icon: "https://storage.googleapis.com/fenado-ai-farm-public/generated/88cbe50a-365c-4774-a5a3-b61cec245adf.png"
  }
];

const FeaturesSection = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <section id="features" className="py-20 bg-background px-4 relative">
      {/* Background elements */}
      <div className="absolute top-40 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-80 h-80 bg-primary/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto max-w-6xl">
        {/* Section header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Powerful Features for <span className="text-gradient">Efficient Management</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Everything you need to streamline civic issue resolution and improve community satisfaction
          </p>
        </motion.div>
        
        {/* Features grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {features.map((feature, index) => (
            <motion.div key={index} variants={itemVariants}>
              <Card className="h-full bg-card border-l-2 border-t-2 border-primary/30 hover:border-primary transition-colors duration-300 overflow-hidden group">
                <CardContent className="p-6">
                  <div className="mb-4 flex justify-center">
                    <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                      <img 
                        src={feature.icon} 
                        alt={feature.title} 
                        className="w-10 h-10 object-contain"
                      />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-center mb-3 text-foreground group-hover:text-primary transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-center">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
        
        {/* Feature showcase */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-20"
        >
          <div className="bg-card border border-border rounded-xl overflow-hidden shadow-lg">
            <div className="p-4 sm:p-6 border-b border-border">
              <h3 className="font-medium text-lg">Issue Management Dashboard</h3>
            </div>
            <div className="p-0">
              <img 
                src="https://storage.googleapis.com/fenado-ai-farm-public/generated/037a6093-6186-4ec5-bd77-6765d085cfc9.png" 
                alt="CivicResolve Dashboard" 
                className="w-full h-auto"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;